import { isInLineLiff, isInLineMiniApp, Platform } from '@play-co/gcinstant';
import { Version } from '@play-co/native-game-js';

export type LineConfig = {
  env: Environment;
  channel: LineChannel;
  channelId: string;
  liffId: string;
  miniAppEndpointUrl: string;
  scopes: string[];
  oaId?: string;
};

function isCustomLiffSubdomain(host: string = window?.location?.host): boolean {
  const subdomain = host?.split('.')[0] || '';

  // Any subdomain with "liff" as a distinct part, e.g. liff.buruburu.app, buruburu-liff.playco.games, buruburu-liff-dev.playco.games
  const isCustomLiffSubdomain = /\bliff\b/.test(subdomain);

  return isCustomLiffSubdomain;
}

// TODO: Review — some of these variants may no longer be reachable.
export type Environment = 'web' | 'native' | 'liff' | 'miniapp';
export type LineChannel = 'login' | 'miniapp';

export function getEnvironment(native?: Platform['legacyNative']): Environment {
  if (native) {
    return 'native';
  }

  if (isInLineLiff()) {
    if (isInLineMiniApp()) {
      return 'miniapp';
    } else {
      return 'liff';
    }
  }

  return 'web';
}

export function getLineChannel(
  env: Environment,
  native: Platform['legacyNative'],
): LineChannel {
  if (isCustomLiffSubdomain()) {
    return 'login';
  }

  switch (env) {
    case 'liff':
    case 'web':
      return 'login';

    case 'native':
      return getNativeAppChannel(native);

    case 'miniapp':
      return 'miniapp';
  }
}

const IOS_APP_VERSION_WITH_CONFIGURABLE_CHANNEL = new Version(2, 13, 1);
const ANDROID_APP_VERSION_USING_LOGIN_CHANNEL = new Version(1, 2, 0);

/**
 * Always returns 'login' — 'game' channel was removed with PlatformLine.
 * TODO: Inline this and remove the version constants above.
 */
export function getNativeAppChannel(
  native: Platform['legacyNative'],
): LineChannel {
  if (!native) {
    throw new Error('Native interface is required to get native app channel');
  }

  return 'login';
}

/**
 * Detect if the native app support LINE login channel
 * iOS app >= 2.13.1 support configurable channel (game or login)
 * Android app >= 1.2.0 support only login channel
 */
export function isNativeSupportLineLoginChannel(
  native: Platform['legacyNative'],
): boolean {
  if (!native) {
    return false;
  }

  if (native.host === 'native-builder' && native.platform.os === 'ios') {
    // Special case for the native builder app on iOS, which is always using the game channel
    return false;
  }

  const currentVersion = Version.parse(
    native.app.version!.split('.').slice(0, 3).join('.'), // must be a valid semver, but Android builds have 4 components
  );
  const minVersionForLoginChannel =
    native.platform.os === 'ios'
      ? IOS_APP_VERSION_WITH_CONFIGURABLE_CHANNEL
      : ANDROID_APP_VERSION_USING_LOGIN_CHANNEL;

  return currentVersion.isGreaterThanOrEqual(minVersionForLoginChannel);
}

// TODO: Channel parameter is unused — all channels now use the same scopes.
export function getLineChannelScopes(_channel: LineChannel): string[] {
  return ['openid', 'profile', 'message.write'];
}

export function getLineConfig(native?: Platform['legacyNative']): LineConfig {
  const env = getEnvironment(native);
  const channel = getLineChannel(env, native);
  const scopes = getLineChannelScopes(channel);
  const miniAppEndpointUrl = process.env.LINE_MINI_APP_ENDPOINT_URL!;
  const oaId = process.env.MIGRATION_LINE_OA_ID;

  switch (channel) {
    case 'login':
      return {
        env,
        channel,
        channelId: process.env.MIGRATION_LINE_LOGIN_CHANNEL_ID!,
        liffId: process.env.MIGRATION_LINE_LOGIN_LIFF_ID!,
        miniAppEndpointUrl,
        scopes,
        oaId,
      };
    case 'miniapp':
      return {
        env,
        channel,
        channelId: process.env.MIGRATION_LINE_MINI_APP_CHANNEL_ID!,
        liffId: process.env.MIGRATION_LINE_MINI_APP_LIFF_ID!,
        miniAppEndpointUrl,
        scopes,
        oaId,
      };
    default:
      throw new Error('Unsupported Line channel: ' + channel);
  }
}
